import React, { useEffect, useState } from 'react';
import PlotContainer from '../../../containers/PlotContainer';
import {
    useGetModelInputOptionsQuery,
    useStartModelFitMutation,
    useGetModelResultsQuery,
    useGetModelChartQuery,
    useUpdateStageStatusMutation,
} from '../../../redux/api/compassPortal';
import { errMsg } from './bits';

// Modelling — the first model over the processed outputs. Spend columns are
// grouped to a sane channel grain BEFORE the fit (platform by default), so the
// output is ~8 real channels, not 40 campaign slices. Presentation is
// CMO-first: one number + one verdict, a ranked contribution chart, an honest
// base-vs-media split. The exact diagnostics (R², adstock, saturation) live in
// a collapsed expander for the MS analyst — present, not shouted.

const money = (n) => '£' + Math.round(Number(n) || 0).toLocaleString();
const moneyBig = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1e6) return '£' + (v / 1e6).toFixed(1) + 'M';
    if (Math.abs(v) >= 1e5) return '£' + (v / 1e3).toFixed(0) + 'k';
    return money(v);
};
const roiBand = (roi) => {
    const r = Number(roi) || 0;
    if (r >= 2) return { label: 'Efficient', color: '#2ED8C3' };
    if (r >= 1) return { label: 'Working', color: '#4EA8DE' };
    return { label: 'Early read', color: '#7C8B99' };
};
// For a count KPI, ROI is units-per-£ (≈always <1) so it can't drive the band.
// Band on cost-per-unit relative to the channels' median (cheaper = better).
const bandForChannel = (c, kind, medianCpu) => {
    if (kind !== 'count') return roiBand(c.roi);
    const cpu = Number(c.cost_per_unit);
    if (!cpu || !medianCpu) return { label: '—', color: '#7C8B99' };
    if (cpu <= medianCpu * 0.6) return { label: 'Efficient', color: '#2ED8C3' };
    if (cpu <= medianCpu * 1.4) return { label: 'Working', color: '#4EA8DE' };
    return { label: 'Costly', color: '#7C8B99' };
};
const GRAINS = [
    { key: 'platform', label: 'By platform (recommended)' },
    { key: 'platform_market', label: 'By platform × market' },
];

export default function ModellingWorkspace({ project, onBack, onComplete, refreshSignal }) {
    const pid = project.project_id;
    const [target, setTarget] = useState('');
    const [grain, setGrain] = useState('platform');
    const [err, setErr] = useState(null);
    const [gateMsg, setGateMsg] = useState(null);
    const [polling, setPolling] = useState(false);

    const { data: options } = useGetModelInputOptionsQuery({ projectId: pid });
    const { data: model, refetch } = useGetModelResultsQuery(
        { projectId: pid }, { pollingInterval: polling ? 3000 : 0 });
    const [startFit] = useStartModelFitMutation();
    const [updateStageStatus, { isLoading: completing }] = useUpdateStageStatusMutation();

    useEffect(() => { setPolling(model?.status === 'running'); }, [model]);
    useEffect(() => {
        if (!target && options?.target_candidates?.length) setTarget(options.target_candidates[0]);
    }, [options, target]);
    useEffect(() => { if (refreshSignal) refetch(); /* eslint-disable-next-line */ }, [refreshSignal]);

    const run = async () => {
        setErr(null);
        try { await startFit({ projectId: pid, targetColumn: target, grain }).unwrap(); refetch(); }
        catch (e) { setErr(errMsg(e)); }
    };
    const complete = async () => {
        setGateMsg(null);
        try {
            await updateStageStatus({ projectId: pid, stage_id: 'data-modelling', status: 'complete' }).unwrap();
            onComplete?.('data-modelling', 'compass-delivery');
        } catch (e) { setGateMsg(errMsg(e)); }
    };

    const res = model?.results;
    const running = model?.status === 'running';
    const ready = (options?.sources_ready || []).length;

    // Derived numbers, tolerant of an older cached result (pre-grouping fields).
    const r2 = res && (res.r_squared ?? res.r2);
    const basePct = res && (res.base_pct ?? 0);
    const mediaPct = res && (res.media_pct ?? Math.round((100 - basePct) * 10) / 10);
    const mediaRev = res && (res.total_modelled_revenue
        ?? (res.channels || []).reduce((a, c) => a + (c.modelled_revenue || 0), 0));
    const totalSpend = res && (res.total_spend
        ?? (res.channels || []).reduce((a, c) => a + (c.total_spend || 0), 0));
    const blendedRoi = res && (res.blended_roi ?? (totalSpend > 0 ? mediaRev / totalSpend : 0));
    const totalModelled = res && mediaPct > 0 ? mediaRev / (mediaPct / 100) : 0;
    const baseRev = res ? totalModelled * (basePct / 100) : 0;
    const channels = res ? [...(res.channels || [])].sort((a, b) => (b.modelled_revenue || 0) - (a.modelled_revenue || 0)) : [];
    const kind = (res && res.target_kind) || 'money';
    const kpi = res && (res.target_column || 'revenue');
    const fmtKpi = (n) => kind === 'money' ? moneyBig(n) : Math.round(Number(n) || 0).toLocaleString();
    const blendedCpu = res && (res.blended_cost_per_unit ?? (mediaRev > 0 ? totalSpend / mediaRev : null));
    const lowSignal = res && (res.low_signal ?? (mediaPct < 5 || (r2 != null && r2 < 0.1)));
    const _cpus = channels.map((c) => Number(c.cost_per_unit)).filter((x) => x > 0).sort((a, b) => a - b);
    const medianCpu = _cpus.length ? _cpus[Math.floor(_cpus.length / 2)] : 0;

    return (
        <>
            <button className="cp-back-btn" onClick={onBack}>← Back to projects</button>
            <div className="cp-toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <p className="cp-section-label" style={{ margin: 0 }}>Modelled Results</p>
                    {res && <span className="cp-chip" title={`R² ${r2}`}>First-pass · Directional</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select className="cp-rule-select" value={target} disabled={running}
                        onChange={(e) => setTarget(e.target.value)}>
                        <option value="">KPI to explain…</option>
                        {(options?.target_candidates || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className="cp-rule-select" value={grain} disabled={running}
                        onChange={(e) => setGrain(e.target.value)}>
                        {GRAINS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </select>
                    <button className="cp-btn cp-btn--primary" disabled={!target || running || !ready} onClick={run}>
                        {running ? 'Fitting…' : res ? 'Re-fit' : 'Run first model'}
                    </button>
                    {res && (
                        <button className="cp-btn cp-btn--ghost" disabled={completing} onClick={complete}>
                            {completing ? 'Saving…' : 'Sign off → Delivery'}
                        </button>
                    )}
                </div>
            </div>
            {err && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{err}</p>}
            {gateMsg && <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>{gateMsg}</p>}
            {model?.status === 'failed' && model?.error && (
                <p style={{ color: 'var(--co-neg)', fontSize: 13 }}>The fit failed — {model.error}</p>
            )}
            {!res && !running && (
                <div className="cp-empty">
                    {!ready
                        ? 'Nothing processed yet — finish the data stages first.'
                        : (options?.target_candidates || []).length === 0
                            ? `${ready} processed source(s) ready, but no revenue/KPI-like column was found in them. The model needs a KPI (e.g. revenue, sales, conversions, orders). Check the processed outputs' column names.`
                            : `${ready} processed source(s) ready. Pick the KPI to explain and run the first model.`}
                </div>
            )}
            {running && (
                <div className="cp-card" style={{ padding: 16, marginBottom: 16 }}>
                    <div className="cp-pullbar"><div className="cp-pullbar-track cp-procbar"><span /></div>
                        <span className="cp-src-note">fitting the model — usually under a minute</span></div>
                </div>
            )}

            {res && (
                <>
                    <p style={{ fontSize: 16, lineHeight: 1.5, margin: '4px 0 14px' }}>
                        Marketing drove <b>{fmtKpi(mediaRev)}</b>{kind === 'money' ? '' : ` ${kpi}`} this period —
                        {' '}<b>{mediaPct}%</b> of the total{lowSignal ? '.' : ' —'}
                        {lowSignal ? '' : kind === 'money'
                            ? <> returning about <b>£{Number(blendedRoi).toFixed(2)}</b> for every £1 invested.</>
                            : <> at about <b>£{blendedCpu != null ? Number(blendedCpu).toFixed(2) : '—'}</b> per {kpi.replace(/s$/, '')}.</>}
                    </p>
                    {lowSignal && (
                        <div className="cp-lowsig">
                            <b>No measurable media effect yet.</b> This model can't yet separate media's impact from
                            baseline demand — media explains only {mediaPct}% of {kpi} (R² {r2}). That usually means a
                            first-pass fit, a short window, or (on test data) spend that isn't linked to sales. Treat the
                            channel figures below as directional; they sharpen with more data and refined grouping.
                        </div>
                    )}
                    {res && ((res.grain && res.grain !== grain) || (res.target_column && res.target_column !== target)) && (
                        <p style={{ color: 'var(--co-neg)', fontSize: 13, marginTop: -8, marginBottom: 12 }}>
                            Showing the model fitted on {res.target_column} at {res.grain === 'platform_market' ? 'platform × market' : 'platform'} grain.
                            Re-fit to apply your current selection.
                        </p>
                    )}
                    {(res.ai_summary || (res.findings || []).length > 0) && !(lowSignal && !res.ai_summary) && (
                        <div className="cp-ai-summary">
                            <div className="cp-ai-head">✦ Summary</div>
                            {res.ai_summary?.headline && <p className="cp-ai-headline">{res.ai_summary.headline}</p>}
                            {lowSignal && <p className="cp-sub" style={{ margin: '0 0 6px' }}>Directional only — the model can't reliably attribute effect yet:</p>}
                            <ul>
                                {(res.ai_summary?.bullets || res.findings || []).map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="cp-hero">
                        <div className="cp-hero-tile cp-hero-tile--star">
                            <div className="cp-hero-num">{fmtKpi(mediaRev)}</div>
                            <div className="cp-hero-label">Media-driven {kpi} · {mediaPct}% of total</div>
                        </div>
                        <div className="cp-hero-tile">
                            <div className="cp-hero-num">{moneyBig(totalSpend)}</div>
                            <div className="cp-hero-label">Total media investment · across {channels.length} channels</div>
                        </div>
                        {lowSignal ? (
                            <div className="cp-hero-tile">
                                <div className="cp-hero-num" style={{ fontSize: 20 }}>No measurable effect yet</div>
                                <div className="cp-hero-label">media explains only {mediaPct}% of {kpi} — directional only</div>
                            </div>
                        ) : kind === 'money' ? (
                            <div className="cp-hero-tile">
                                <div className="cp-hero-num">£{Number(blendedRoi).toFixed(2)}<span style={{ fontSize: 13, fontWeight: 400 }}> / £1</span></div>
                                <div className="cp-hero-label">Blended ROI · £1.00 is break-even</div>
                            </div>
                        ) : (
                            <div className="cp-hero-tile">
                                <div className="cp-hero-num">£{blendedCpu != null ? Number(blendedCpu).toFixed(2) : '—'}</div>
                                <div className="cp-hero-label">Cost per {kpi.replace(/s$/, '')} · media spend ÷ modelled {kpi}</div>
                            </div>
                        )}
                    </div>

                    <div className="cp-card" style={{ padding: '14px 16px', marginBottom: 16 }}>
                        <div className="cp-split">
                            <span className="cp-split-base" style={{ width: `${basePct}%` }}>Base {basePct}%</span>
                            <span className="cp-split-media" style={{ width: `${mediaPct}%` }}>Media {mediaPct}%</span>
                        </div>
                        <p className="cp-sub" style={{ margin: '8px 0 0' }}>
                            Most {kpi} ({basePct}%) is baseline demand — brand and existing
                            demand that arrives without this period's media. The {mediaPct}% is what these campaigns added on top
                            (60–80% baseline is normal for an established brand).
                        </p>
                    </div>

                    <div className="cp-card" style={{ padding: 8, marginBottom: 16, maxHeight: 600, overflow: 'hidden' }}>
                        <PlotContainer
                            tooltipTitle="Incremental contribution by channel — colour shows efficiency"
                            useApi={useGetModelChartQuery}
                            useApiArgs={{ projectId: pid, v: res.fitted_at }}
                        />
                    </div>

                    <div className="cp-table-scroll">
                        <table className="cp-table">
                            <thead><tr>
                                <th>Channel</th><th>Spend</th><th>Incremental {kpi}</th>
                                <th>Share of {kpi}</th><th>{kind === 'money' ? 'ROI, per £1' : `Cost per ${kpi.replace(/s$/, '')}`}</th><th>Notes</th>
                            </tr></thead>
                            <tbody>
                                {channels.map((c) => {
                                    const b = bandForChannel(c, kind, medianCpu);
                                    return (
                                        <tr key={c.group_key || c.channel}>
                                            <td><span className="cp-roi-chip" style={{ background: b.color }} />{c.display || c.channel}</td>
                                            <td>{money(c.total_spend)}</td>
                                            <td>{fmtKpi(c.modelled_revenue)}</td>
                                            <td>{c.contribution_pct}%</td>
                                            <td>{kind === 'money'
                                                ? `£${Number(c.roi || 0).toFixed(2)}`
                                                : (c.cost_per_unit != null ? `£${Number(c.cost_per_unit).toFixed(2)}` : '—')}</td>
                                            <td className="cp-src-note">
                                                {kind === 'money' ? ((Number(c.roi) || 0) < 1 ? 'early read' : '') : (b.label === 'Costly' ? 'costly' : '')}
                                            </td>
                                        </tr>
                                    );
                                })}
                                <tr style={{ opacity: 0.7 }}>
                                    <td>Baseline (non-media)</td>
                                    <td>—</td>
                                    <td>{mediaPct >= 5 && baseRev ? fmtKpi(baseRev) : '—'}</td>
                                    <td>{basePct}%</td>
                                    <td>—</td>
                                    <td className="cp-src-note">brand, seasonality, demand</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="cp-sub" style={{ marginTop: 8 }}>
                        Figures are model estimates of incremental effect, not tracked sales.
                        {kind === 'money'
                            ? ' A £0.00 return means no measurable effect yet — on a first model usually low spend or channels that move together, not wasted budget.'
                            : ' Cost per acquisition is the modelled media spend divided by the extra ' + kpi + ' it drove.'}
                    </p>

                    <details className="cp-card" style={{ padding: '12px 16px' }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13 }}>Model details &amp; method (for Marketing Science)</summary>
                        <p className="cp-sub" style={{ marginTop: 10 }}>
                            R² {r2} · base {basePct}% · {channels.length} channels · grain {res.grain || 'platform'} ·
                            {' '}{res.rows?.toLocaleString?.() || '—'} days · fitted by {res.fitted_by || '—'}.
                            First, deliberately simple model (adstock carryover → diminishing returns → non-negative
                            least squares). Use it to steer budget direction, not to forecast exact £ — confidence
                            rises as data and channel grouping are refined. Marketing Science tunes and signs off
                            before anything reaches the client.
                        </p>
                        {(res.dropped_channels || []).length > 0 && (
                            <p className="cp-sub" style={{ marginTop: 0 }}>
                                {res.dropped_channels.length} channel(s) folded in — they move in step with a bigger
                                channel so the model can't separate them (their spend is still counted in the totals):
                                {' '}{res.dropped_channels.map((d) => d.display).join(', ')}.
                            </p>
                        )}
                        <div className="cp-table-wrap" style={{ marginTop: 10 }}>
                            <table className="cp-table">
                                <thead><tr><th>Channel</th><th>Adstock</th><th>Saturation k</th><th>Coefficient</th></tr></thead>
                                <tbody>
                                    {channels.map((c) => (
                                        <tr key={c.group_key || c.channel}>
                                            <td>{c.display || c.channel}</td>
                                            <td>{c.adstock_rate}</td>
                                            <td>{c.saturation_k}</td>
                                            <td>{c.coefficient}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </details>
                </>
            )}
        </>
    );
}
